import React from 'react'
import { Link } from 'react-router-dom'

import { truncateMiddle } from '@/shared/lib/format'
import { isAddress, isBlockHeight, isTxHash } from '@/shared/lib/validators'
import CopyButton from '@/shared/ui/CopyButton'

type HashKind = 'tx' | 'address' | 'block'

interface HashLinkProps {
  kind: HashKind
  value: string | number | null | undefined
  /** 是否截断显示，默认 true。 */
  truncate?: boolean
  /** 是否显示复制按钮，默认 true。 */
  copyable?: boolean
  /** 字号，默认使用 CSS 变量 --font-size-lg（14px）。 */
  fontSize?: React.CSSProperties['fontSize']
  /** 复制按钮图标尺寸。 */
  copySize?: number
  /** 自定义文本（如 validator 名）；不提供则用 value。 */
  label?: string
  style?: React.CSSProperties
}

const buildHref = (kind: HashKind, value: string): string => {
  switch (kind) {
    case 'tx':
      return `/tx/${value}`
    case 'address':
      return `/address/${value}`
    case 'block':
      return `/block/${value}`
  }
}

const isValid = (kind: HashKind, value: string): boolean => {
  if (kind === 'tx') return isTxHash(value)
  if (kind === 'address') return isAddress(value)
  return isBlockHeight(value)
}

/**
 * 哈希/地址/区块的统一链接展示：内置校验、截断与复制按钮。
 * 校验失败时只渲染纯文本，避免把任意字符串作为 URL 跳转。
 */
const HashLink: React.FC<HashLinkProps> = ({
  kind,
  value,
  truncate = true,
  copyable = true,
  fontSize = 'var(--font-size-lg)',
  copySize = 12,
  label,
  style,
}) => {
  const raw = value == null ? '' : String(value).trim()
  if (!raw) return <span style={{ color: 'var(--app-text-secondary)' }}>—</span>

  const display = label ?? (truncate ? truncateMiddle(raw) : raw)
  const linkStyle: React.CSSProperties = {
    color: 'var(--accent-cyan)',
    textDecoration: 'underline',
    fontFamily: kind === 'block' ? undefined : 'monospace',
    fontSize,
    ...style,
  }

  if (!isValid(kind, raw)) {
    return (
      <span
        style={{
          fontFamily: kind === 'block' ? undefined : 'monospace',
          fontSize,
          color: 'var(--app-text-tertiary)',
          wordBreak: 'break-all',
        }}
        title={`Invalid ${kind} value`}
      >
        {display}
      </span>
    )
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <Link to={buildHref(kind, raw)} style={linkStyle}>
        {display}
      </Link>
      {copyable && <CopyButton text={raw} size={copySize} label={`Copy ${kind}`} />}
    </span>
  )
}

export default HashLink
